/**
 * Subscription and Billing
 *
 * Three things this page must not do, all of which it used to.
 *
 * It must not invent the current plan. It used to read localStorage with a
 * default of "standard", so it could tell a Free account it was on Standard
 * while the server capped it at two alerts. The plan now comes from the same
 * claim the backend enforces.
 *
 * It must not promise what the product cannot deliver. The old feature lists
 * sold SMS alerts (no provider exists) and "real-time" data (prices arrive by
 * weekly survey, so nothing here is real time). Every line below is something
 * we do today, or is marked as coming.
 *
 * History depth is deliberately NOT sold here. It was a paid line until
 * 2026-08-25, when it became clear that capping weekly survey data leaves
 * four points on a chart, which breaks the product for the people being
 * asked to buy it. Everyone reads the whole archive; the ladder is capacity
 * and tools.
 *
 * It must not collect card details it cannot process. Online payment is not
 * wired up yet, so the demo checkout form is gone rather than sitting on a
 * live site asking real customers for card numbers.
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Card, CardContent, Chip, Divider,
  Alert as MuiAlert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, EmptyState } from '../../components/common';
import { useRecipes } from '../../theme/recipes';

// Needs to exist as a real inbox before launch. Until online payment is
// wired, this is the only way someone can actually change plan.
const SALES_CONTACT = 'sales@farmgate.ng';

// Where each plan sits on the ladder, so a plan is only ever recommended to
// somebody it would actually be a step up for. Recommending Standard to a
// Premium customer is recommending a downgrade, which reads as a mistake and
// undermines the page. Admins are not on a plan at all and sit below the
// bottom rung, so they see the ladder as a prospect would.
const PLAN_RANK = { Free: 0, Standard: 1, Premium: 2 };
const rankOf = (plan) => (plan in PLAN_RANK ? PLAN_RANK[plan] : -1);

/**
 * The plans, worded as capability rather than marketing. Anything not yet
 * built carries `pending: true` and is shown greyed with a "coming" note, so
 * the page can advertise the roadmap without claiming it already works.
 *
 * These mirror libs/common/farmgate_common/plans.py, which enforces them.
 */
const PLANS = [
  {
    id: 'Free',
    name: 'Free',
    price: '₦0',
    cadence: 'forever',
    description: 'For following a few commodities and getting told when prices move.',
    features: [
      { text: 'All public market pages' },
      { text: '2 active price alerts, delivered by email' },
      { text: '3 commodities in your portfolio' },
      { text: 'The complete price history, same as every plan' },
    ],
  },
  {
    id: 'Standard',
    name: 'Standard',
    price: '₦15,000',
    cadence: '/month',
    description: 'For traders working the market daily.',
    recommended: true,
    features: [
      { text: '20 active price alerts, ten times the free allowance' },
      { text: 'Unlimited portfolio holdings' },
      { text: 'A Monday morning digest of the commodities you follow' },
      { text: 'Export up to 5,000 rows a month' },
    ],
  },
  {
    id: 'Premium',
    name: 'Premium',
    price: '₦45,000',
    cadence: '/month',
    description: 'For businesses feeding Farmgate data into their own systems.',
    features: [
      { text: 'Everything in Standard' },
      { text: 'Unlimited price alerts' },
      { text: 'Unlimited export, including Excel and JSON' },
      { text: 'A read API over the same data, 100,000 requests a month' },
      { text: 'Five API keys you create and revoke yourself' },
      { text: 'Priority support' },
    ],
  },
];

function Subscription() {
  const { palette } = useThemeMode();
  const { user } = useAuth();
  const recipes = useRecipes();

  // The plan the server enforces. Admins are not on a plan at all.
  const isAdmin = user?.role === 'admin';
  const currentPlan = isAdmin ? null : (user?.plan || 'Free');

  const ctaSx = { ...recipes.primaryBtnSx, py: 1.1, fontSize: '0.9rem', textTransform: 'none' };

  const mailto = (plan) =>
    `mailto:${SALES_CONTACT}?subject=${encodeURIComponent(`Farmgate ${plan} plan`)}` +
    `&body=${encodeURIComponent(
      `Hello,\n\nI would like to move to the ${plan} plan.\n\nAccount: ${user?.email || ''}\n`
    )}`;

  return (
    <Box>
      <PageHeader
        eyebrow="Account"
        title="Plans and billing"
        subtitle="What each plan includes, and what you are on today"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Plans' },
        ]}
      />

      <MuiAlert
        severity="info"
        icon={<Icon icon="ph:info" width={20} height={20} />}
        sx={{
          mb: 3, bgcolor: palette.infoBg,
          border: `1px solid ${palette.info}30`, color: palette.text,
          '& .MuiAlert-icon': { color: palette.info },
        }}
      >
        Paying online is not available yet. To change plan, email{' '}
        <Box component="a" href={`mailto:${SALES_CONTACT}`} sx={{ color: palette.accent, fontWeight: 600 }}>
          {SALES_CONTACT}
        </Box>{' '}
        and we will set it up for you.
      </MuiAlert>

      {isAdmin && (
        <MuiAlert severity="info" sx={{ mb: 3 }}>
          Admin accounts are not on a plan. Everything below describes what customers get.
        </MuiAlert>
      )}

      {/* Plans */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          // Only suggest a plan that is above the one they already have.
          const suggested = plan.recommended && rankOf(plan.id) > rankOf(currentPlan);
          const highlighted = isCurrent || suggested;
          return (
            <Grid item xs={12} md={4} key={plan.id}>
              <Card
                sx={{
                  height: '100%',
                  border: `1.5px solid ${highlighted ? palette.accent : palette.cardBorder}`,
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': { boxShadow: palette.shadowMd },
                }}
              >
                {suggested && (
                  <Chip
                    icon={<Icon icon="ph:star" width={12} height={12} />}
                    label="RECOMMENDED"
                    size="small"
                    sx={{ position: 'absolute', top: -12, right: 16, bgcolor: palette.accentBg, color: palette.accent, border: `1px solid ${palette.accent}`, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', height: 24, '& .MuiChip-icon': { color: palette.accent } }}
                  />
                )}
                {isCurrent && (
                  <Chip
                    icon={<Icon icon="ph:check" width={12} height={12} color="#fff" />}
                    label="YOUR PLAN"
                    size="small"
                    sx={{ position: 'absolute', top: -12, right: 16, bgcolor: palette.accent, color: '#fff', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', height: 24 }}
                  />
                )}
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: palette.text, mb: 0.3 }}>{plan.name}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary, mb: 2, lineHeight: 1.5, minHeight: 40 }}>
                    {plan.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 2.5 }}>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '2rem', color: palette.text, letterSpacing: '-0.02em' }}>
                      {plan.price}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: palette.textMuted }}>{plan.cadence}</Typography>
                  </Box>
                  <Divider sx={{ borderColor: palette.divider, mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3, minHeight: 180 }}>
                    {plan.features.map((f) => (
                      <Box key={f.text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ mt: 0.2, flexShrink: 0, color: f.pending ? palette.textMuted : palette.accent }}>
                          <Icon icon={f.pending ? 'ph:clock' : 'ph:check'} width={15} height={15} />
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', color: f.pending ? palette.textMuted : palette.textSecondary, lineHeight: 1.5 }}>
                          {f.text}
                          {f.pending && (
                            <Box component="span" sx={{ fontStyle: 'italic' }}> (not built yet)</Box>
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {isCurrent ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      disableElevation
                      disabled
                      sx={{ fontWeight: 700, borderRadius: '10px', textTransform: 'none', py: 1.1, color: palette.textMuted, borderColor: palette.cardBorder }}
                    >
                      Your current plan
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      disableElevation
                      href={mailto(plan.name)}
                      sx={ctaSx}
                    >
                      {plan.id === 'Free' ? 'Ask to move to Free' : `Ask about ${plan.name}`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Billing history */}
      <Card>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Billing history</Typography>
        </Box>
        <CardContent>
          <EmptyState
            variant="dashed"
            icon={<Icon icon="ph:receipt" width={26} height={26} />}
            title="No invoices yet"
            description="Invoices will appear here once online payment is live. Plan changes arranged by email are not billed through the platform."
          />
        </CardContent>
      </Card>

      <Typography sx={{ mt: 3, fontSize: '0.8rem', color: palette.textMuted }}>
        Limits are enforced on our side, so what a plan says here is what your account can do. See{' '}
        <Box component={RouterLink} to="/app/settings" sx={{ color: palette.accent, fontWeight: 600, textDecoration: 'none' }}>
          Settings
        </Box>{' '}
        to control alert emails.
      </Typography>
    </Box>
  );
}

export default Subscription;
